import { css, html, LitElement } from 'lit';
import { customElement } from 'lit/decorators.js';

import { Task } from '@lit/task';
import { Temporal } from 'temporal-polyfill';
import { api } from './api.js';
import { pageStyles } from './components/cssstyles.js';
import { platformUrl } from './platformUrl.js';
import { RouteConfig } from './router.js';
import { tasks } from './tasks.js';

// Deployment counters are bucketed by UTC day, so the charts are rendered in
// UTC rather than in the viewer's timezone.
const TZ_UTC = 'UTC';

@customElement('page-administration-usage')
export class PageAdministrationUsage extends LitElement {

  static routes(): RouteConfig[] {
    return [{
      path: [
        '/administration/usage',
      ],
      render: () => html`<page-administration-usage></page-administration-usage>`,
    }];
  }

  static url(): string {
    return platformUrl('/administration/usage');
  }

  private _statsTask = new Task(this, {
    args: () => [] as const,
    task: async ([], { signal }) => {
      const stats = await api.administration.deploymentStats.GET(undefined, { signal });
      // Counting started when the deployment_counters table landed, so the
      // range is the whole history: from the first bucket up to today.
      const today = Temporal.Now.plainDateISO(TZ_UTC);
      const days = stats.daily.map(metric => Temporal.PlainDate.from(metric.day));
      const since = days.length ? days[0] : today;
      const until = days.reduce((a, b) => Temporal.PlainDate.compare(a, b) < 0 ? b : a, today);
      return { ...stats, since, until };
    },
  });

  private _pageMetadataTask = tasks.pageMetadata(this, {
    args: () => [],
    metadata: () => ({ title: 'Deployment Usage · Dashboard' }),
  });

  override render() {
    return html`
      <user-header submenu=admin-usage></user-header>
      <app-body wide>
        <usage-totals
          heading="Deployment Usage"
          description="Deployment-wide counters across every organization, including data that has since been deleted by data retention."
          .metrics=${this._statsTask.value?.daily}
        ></usage-totals>
        <usage-metrics
          .metrics=${this._statsTask.value?.daily}
          .since=${this._statsTask.value?.since}
          .until=${this._statsTask.value?.until}
        ></usage-metrics>
      </app-body>
      <app-footer></app-footer>
    `
  }

  static styles = [pageStyles, css`
    :host {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-medium);
    }
  `];
}
